import {Graphics, Matrix, Point, RenderTexture, Sprite, Texture} from 'pixi.js';
import {CardData} from '../data/active_data/field_config/card_stack_data/CardData';
import {TextureUrlLoader} from '../utils/TextureUrlLoader';
import {DynamicData} from '../data/DynamicData';
import {Pivot} from '../utils/Pivot';
import {DrawMenu} from './draw_holder/DrawMenu';
import {DrawInstrumentMenuData, DrawInstrumentType} from './draw_holder/draw_menu/draw_buttons/DrawInstrumentMenuData';
import {DrawHolderEvent} from './draw_holder/draw_menu/events/DrawHolderEvent';
import {DrawColor} from './draw_holder/draw_menu/instument_top_block/color_picker/DrawColor';
import {IInstrument} from './draw_holder/instuments/IInstrument';
import {InstrumentBrush} from './draw_holder/instuments/InstrumentBrush';
import {SliderIconType} from './draw_holder/draw_menu/instument_top_block/slider/SliderIconType';
import {InstrumentEraser} from './draw_holder/instuments/InstrumentEraser';
import {ColorOverlayFilter} from 'pixi-filters';
import {PixiApplicationController} from '../controllers/PixiApplicationController';
import {Timeout} from '../utils/Timeout';
import {InstrumentText} from './draw_holder/instuments/InstrumentText';
import {TextCursor} from './draw_holder/instuments/instrument_text/TextCursor';
import {SmoothGraphics} from '@pixi/graphics-smooth';

export class DrawHolder extends Sprite
{
    private card: Sprite;
    private cardMask: Sprite;
    private cardRender: Sprite;
    private cardRenderContainer: Sprite;
    private cardHolder: Sprite;
    private back: Graphics;
    private drawMenu: DrawMenu;
    private onSelectInstrumentBindThis: (e: Event) => void;
    private onSelectSizeBindThis: (e: Event) => void;
    private onSelectColorBindThis: (e: Event) => void;
    private onTextDoneBindThis: (e: Event) => void;
    private onNeedBackupBindThis: (e: Event) => void;
    private currentInstrumentMenuData: DrawInstrumentMenuData | undefined;
    private currentInstrument: IInstrument | undefined;
    private currentSize: number = 0;
    private currentDrawColor: DrawColor = DrawColor.RED;
    private instrumentBrush: InstrumentBrush;
    private instrumentEraser: InstrumentEraser;
    private instrumentText: InstrumentText;

    private brushGraphics: SmoothGraphics;
    private eraseGraphics: SmoothGraphics;
    private textCursor: TextCursor;

    private instrumentsData: DrawInstrumentMenuData[] = [
        {
            type: DrawInstrumentType.POINTER,
            sliderIconType: SliderIconType.ROUND_FILLED
        },
        {
            type: DrawInstrumentType.BRUSH,
            useColor: true,
            useSize: true,
            sliderIconType: SliderIconType.ROUND_FILLED
        },
        {
            type: DrawInstrumentType.TEXTFIELD,
            useColor: true,
            useSize: true,
            sliderIconType: SliderIconType.TEXT
        },
        {
            type: DrawInstrumentType.ERASER,
            useSize: true,
            sliderIconType: SliderIconType.ROUND
        },
        {
            type: DrawInstrumentType.TRASH,
            sliderIconType: SliderIconType.ROUND_FILLED
        }
    ];
    private filter: ColorOverlayFilter;
    private cardMaskTexture: RenderTexture | undefined;
    private cardRenderTexture: RenderTexture | undefined;
    private brushGraphicsContainer: Sprite;
    private eraseGraphicsContainer: Sprite;
    private textCursorContainer: Sprite;
    private brushGraphicsMaskContainer: Sprite;
    private backupTexture: RenderTexture | undefined;
    private backupSprite: Sprite;

    constructor() {
        super();

        this.filter = new ColorOverlayFilter();
        this.filter.color = 0xffffff;

        this.back = new Graphics();
        this.cardHolder = new Sprite();
        this.cardRender = new Sprite();
        this.cardRenderContainer = new Sprite();
        this.card = new Sprite();
        this.cardMask = new Sprite();

        this.brushGraphicsContainer = new Sprite();
        this.eraseGraphicsContainer = new Sprite();
        this.textCursorContainer = new Sprite();
        this.brushGraphicsMaskContainer = new Sprite();
        this.brushGraphics = new SmoothGraphics();
        this.eraseGraphics = new SmoothGraphics();
        this.textCursor = new TextCursor(this.card);
        this.backupSprite = new Sprite();

        //this.brushGraphics.cacheAsBitmap = true;
        //this.eraseGraphics.cacheAsBitmap = true;
        this.brushGraphicsMaskContainer.interactive = false;

        this.instrumentBrush = new InstrumentBrush(this.card, this.brushGraphics, this.brushGraphicsContainer);
        this.instrumentEraser = new InstrumentEraser(this.card, this.eraseGraphics, this.eraseGraphicsContainer);
        this.instrumentText = new InstrumentText(this.card, this.textCursor, this.textCursorContainer);

        this.onSelectInstrumentBindThis = this.onSelectInstrument.bind(this);
        this.onSelectSizeBindThis = this.onSelectSize.bind(this);
        this.onSelectColorBindThis = this.onSelectColor.bind(this);
        this.onTextDoneBindThis = this.onTextDone.bind(this);
        this.onNeedBackupBindThis = this.onNeedBackup.bind(this);
        addEventListener(DrawHolderEvent.SELECT_INSTRUMENT, this.onSelectInstrumentBindThis);
        addEventListener(DrawHolderEvent.SELECT_SIZE, this.onSelectSizeBindThis);
        addEventListener(DrawHolderEvent.SELECT_COLOR, this.onSelectColorBindThis);
        addEventListener(DrawHolderEvent.TEXT_DONE, this.onTextDoneBindThis);
        addEventListener(DrawHolderEvent.NEED_BACKUP, this.onNeedBackupBindThis);

        this.drawMenu = new DrawMenu(this.instrumentsData);

        this.addChild(this.back);
        this.addChild(this.cardHolder).y = -50;
        this.cardHolder.addChild(this.card);
        this.cardHolder.addChild(this.cardRenderContainer);
        this.cardHolder.addChild(this.brushGraphicsMaskContainer);
        this.cardRenderContainer.addChild(this.cardRender);
        this.brushGraphicsMaskContainer.addChild(this.brushGraphicsContainer);
        this.brushGraphicsMaskContainer.addChild(this.textCursorContainer);

        this.brushGraphicsContainer.addChild(this.brushGraphics);
        this.eraseGraphicsContainer.addChild(this.eraseGraphics);
        this.textCursorContainer.addChild(this.textCursor);

        this.cardHolder.addChild(this.cardMask);

        //this.addChild(this.drawMenu);

        this.back.interactive = true;

        this.init();
    }

    /**
     * Вызвать событие показа карты
     * @param cardData - данные карты
     */
    async zoomCard(cardData: CardData): Promise<void> {
        let texture: Texture = (await TextureUrlLoader.load(cardData.flipped ? cardData.backface_src : cardData.face_src))!;
        if (this.cardMaskTexture) {
            this.cardMaskTexture.destroy(true);
        }
        this.cardMaskTexture && this.cardMaskTexture.destroy(true);
        this.cardRenderTexture && this.cardRenderTexture.destroy(true);

        this.cardMaskTexture = RenderTexture.create(texture.width, texture.height);
        this.cardRenderTexture = RenderTexture.create(texture.width, texture.height);
        this.backupTexture = RenderTexture.create(texture.width, texture.height);
        this.backupSprite.texture = this.backupTexture;
        this.backupSprite.width = this.backupSprite.width;
        Pivot.centerPivot(this.backupSprite);

        let tempCard: Sprite = new Sprite(texture);
        let tempCardHolder: Sprite = new Sprite();
        this.card.texture = texture;
        this.card.width = this.card.width;

        tempCardHolder.addChild(tempCard);
        tempCard.filters = [this.filter];
        PixiApplicationController.app.renderer.render(tempCardHolder, {renderTexture: this.cardMaskTexture});
        tempCard.filters = null;
        tempCardHolder.removeChild(tempCard);
        tempCardHolder.destroy();
        tempCard.destroy();

        this.cardRender.texture = this.cardRenderTexture;
        this.cardMask.texture = this.cardMaskTexture;

        this.cardMask.width = this.cardMask.width;
        this.cardRender.width = this.cardRender.width;

        this.brushGraphicsMaskContainer.mask = this.cardMask;
        this.cardRenderContainer.mask = this.cardMask;

        this.currentInstrument && this.currentInstrument.setRender(this.cardRender);

        Pivot.centerPivot(this.card);
        Pivot.centerPivot(this.cardMask);
        Pivot.centerPivot(this.cardRender);

        this.resize();
    }

    resize(): void {
        let height200: number = DynamicData.sceneSizes.height - 200;
        let width200: number = DynamicData.sceneSizes.width - 200;

        this.drawMenu.y = DynamicData.sceneSizes.height / 2 - 35;

        this.x = Math.floor(DynamicData.sceneSizes.width / 2);
        this.y = Math.floor(DynamicData.sceneSizes.height / 2);

        this.back.clear();
        this.back.beginFill(0x0, .5);
        this.back.drawRect(-Math.floor(DynamicData.sceneSizes.width / 2) - 2, -Math.floor(DynamicData.sceneSizes.height / 2) - 2, DynamicData.sceneSizes.width + 4, DynamicData.sceneSizes.height + 4);
        this.back.endFill();

        if (!this.card.texture) {
            return;
        }

        let cardProps: number = this.card.texture.width / this.card.texture.height;
        let sceneProps: number = width200 / height200;

        this.cardHolder.scale.set(cardProps > sceneProps ? width200 / this.card.texture.width : height200 / this.card.texture.height);
    }

    destroy(): void {
        removeEventListener(DrawHolderEvent.SELECT_INSTRUMENT, this.onSelectInstrumentBindThis);
        removeEventListener(DrawHolderEvent.SELECT_SIZE, this.onSelectSizeBindThis);
        removeEventListener(DrawHolderEvent.SELECT_COLOR, this.onSelectColorBindThis);
        removeEventListener(DrawHolderEvent.TEXT_DONE, this.onTextDoneBindThis);
        removeEventListener(DrawHolderEvent.NEED_BACKUP, this.onNeedBackupBindThis);

        delete this.onSelectInstrumentBindThis;
        delete this.onSelectSizeBindThis;
        delete this.onSelectColorBindThis;
        delete this.onTextDoneBindThis;
        delete this.onNeedBackupBindThis;

        this.cardHolder.removeChild(this.cardMask);
        this.cardMask.destroy({children: true, texture: true, baseTexture: true});
        delete this.cardMask;

        super.destroy({children: true});
    }

    private onSelectInstrument(e: Event): void {
        let drawInstrumentMenuData: DrawInstrumentMenuData = (e as MessageEvent).data.instrument;
        this.processSelectInstrument(drawInstrumentMenuData);
    }

    private onNeedBackup(e: Event): void {
        let transform: Matrix = new Matrix();
        transform.tx = Math.floor(this.card.width / 2);
        transform.ty = Math.floor(this.card.height / 2);
        PixiApplicationController.app.renderer.render(this.cardRender, {renderTexture: this.backupTexture, transform});
    }

    private onTextDone(e: Event): void {
        this.selectBrush();
    }

    private onSelectSize(e: Event): void {
        this.currentSize = (e as MessageEvent).data.size;
        if (this.currentInstrument) {
            this.currentInstrument.setSize(this.currentSize);
        }
    }

    private onSelectColor(e: Event): void {
        let drawColor: DrawColor = (e as MessageEvent).data.drawColor;
        if (this.currentDrawColor == drawColor) {
            return;
        }
        this.currentDrawColor = drawColor;
        if (this.currentInstrument) {
            this.currentInstrument.setDrawColor(this.currentDrawColor);
        }
    }

    private init() {
        //this.drawMenu.instrumentButtons.onInstrumentClick(this.instrumentsData.find(instrumentData => instrumentData.type == DrawInstrumentType.BRUSH)!);

        this.drawMenu.instrumentTopBlock.colorPicker.onColorClicked(DrawColor.RED);

        let slider = this.drawMenu.instrumentTopBlock.slider.sliderButton;
        dispatchEvent(new MessageEvent(DrawHolderEvent.SELECT_SIZE, {data: {size: slider.x / slider.dragEndX}}));
    }

    private async processSelectInstrument(drawInstrumentMenuData: DrawInstrumentMenuData): Promise<void> {
        if (this.currentInstrumentMenuData == drawInstrumentMenuData) {
            return;
        }
        this.currentInstrumentMenuData = drawInstrumentMenuData;
        if (this.currentInstrument) {
            this.currentInstrument.stop();
            this.cardHolder.removeChild(this.currentInstrument.getCursor());
        }
        switch (drawInstrumentMenuData.type) {
            case DrawInstrumentType.BRUSH:
                this.currentInstrument = this.instrumentBrush;
                this.cardHolder.addChild(this.currentInstrument!.getCursor());
                let local: Point = this.cardHolder.toLocal(PixiApplicationController.app.renderer.plugins.interaction.mouse.global);
                this.currentInstrument!.getCursor().position.set(local.x, local.y);
                break;
            case DrawInstrumentType.TEXTFIELD:
                this.currentInstrument = this.instrumentText;
                this.cardHolder.addChild(this.currentInstrument!.getCursor());
                break;
            case DrawInstrumentType.ERASER:
                this.currentInstrument = this.instrumentEraser;
                this.cardHolder.addChild(this.currentInstrument!.getCursor());
                break;
            case DrawInstrumentType.TRASH:
                console.log('nb1');
                dispatchEvent(new MessageEvent(DrawHolderEvent.NEED_BACKUP));
                let empty: Sprite = new Sprite();
                PixiApplicationController.app.renderer.render(empty, {
                    renderTexture: this.cardRenderTexture,
                    clear: true
                });
                empty.destroy();
                await Timeout.milliseconds(100);
                this.selectBrush();
                return;
            case DrawInstrumentType.POINTER:
                this.undo();
                await Timeout.milliseconds(100);
                this.selectBrush();
                return;
        }

        if (!this.currentInstrument) {
            return;
        }

        this.currentInstrument.setSize(this.currentSize);
        this.currentInstrument.setDrawColor(this.currentDrawColor);
        this.currentInstrument.start();
        this.currentInstrument.setRender(this.cardRender!);
    }

    private selectBrush(): void {
        this.drawMenu.instrumentButtons.onInstrumentClick(this.instrumentsData.find(instrumentData => instrumentData.type == DrawInstrumentType.BRUSH)!);
    }

    undo():void {
        let transform: Matrix = new Matrix();
        transform.tx = Math.floor(this.card.width / 2);
        transform.ty = Math.floor(this.card.height / 2);
        //storing currentRender
        let tempTexture: RenderTexture = RenderTexture.create(this.card.width, this.card.height);
        let tempSprite: Sprite = new Sprite(tempTexture);
        Pivot.centerPivot(tempSprite);
        PixiApplicationController.app.renderer.render(this.backupSprite, {renderTexture: tempTexture, transform});
        //restoringBackupSprite
        PixiApplicationController.app.renderer.render(this.cardRender, {renderTexture: this.backupTexture, transform});
        //restoringBackupSprite
        PixiApplicationController.app.renderer.render(tempSprite, {renderTexture: this.cardRenderTexture, transform});
    }
}
